/**
 * Reparto de barras en carriles, para que dos camiones a la vez en el mismo
 * muelle no se tapen.
 *
 * El Gantt de producción pinta UNA barra por fila porque una orden no se solapa
 * consigo misma. Un muelle sí: el cuadro es precisamente el sitio donde se ve
 * que dos camiones se han programado encima, y taparlos escondería el problema
 * que la pantalla existe para enseñar.
 */

export interface BarraColocada<T> {
  item: T
  /** 0 = el carril de arriba. */
  carril: number
  left: number
  width: number
}

export interface RepartoCarriles<T> {
  barras: BarraColocada<T>[]
  /** Cuántos carriles hicieron falta. 0 si no hay ninguna barra visible. */
  carriles: number
  /** Las que caían enteras fuera del rango visible y no se pintan. */
  fuera: T[]
}

interface Rango {
  iniMs: number
  finMs: number
}

/**
 * Coloca las barras en el menor número de carriles posible sin que dos que se
 * solapen compartan carril.
 *
 * Es un coloreado voraz de grafo de intervalos: ordenadas por inicio, cada barra
 * va al carril más alto cuyo último ocupante ya haya terminado.
 *
 * **El solape se mide en milisegundos sobre las fechas de origen, NUNCA en
 * porcentaje del carril.** Dos camiones separados por diez minutos están a un
 * 0,17 % en la vista semanal: decidir con eso los pondría en carriles distintos
 * unas veces sí y otras no, según de qué lado cayera el redondeo, y el cuadro
 * cambiaría de aspecto sin que cambiara ningún dato.
 *
 * **Frontera cerrada-abierta**: un camión que termina a las 10:00 y otro que
 * empieza a las 10:00 NO se solapan y comparten carril. Es el turno de muelle
 * real: el uno sale y el otro entra.
 *
 * El desempate de la ordenación es `inicio asc → fin desc → clave asc`, para que
 * el reparto sea determinista. Sin él, un refetch que devolviera los mismos
 * camiones en otro orden los reordenaría en pantalla sin que nada hubiera
 * cambiado.
 */
export function repartirCarriles<T>(
  items: T[],
  rango: (item: T) => Rango,
  pos: (iniMs: number, finMs: number) => { left: number; width: number } | null,
  clave: (item: T) => string,
): RepartoCarriles<T> {
  const ordenados = [...items].sort((a, b) => {
    const ra = rango(a)
    const rb = rango(b)
    if (ra.iniMs !== rb.iniMs) return ra.iniMs - rb.iniMs
    if (ra.finMs !== rb.finMs) return rb.finMs - ra.finMs
    return clave(a).localeCompare(clave(b))
  })

  const barras: BarraColocada<T>[] = []
  const fuera: T[] = []
  /** Fin del último ocupante de cada carril. */
  const finDeCarril: number[] = []

  for (const item of ordenados) {
    const { iniMs, finMs } = rango(item)
    const p = pos(iniMs, finMs)
    if (!p) {
      fuera.push(item)
      continue
    }
    let carril = finDeCarril.findIndex((fin) => fin <= iniMs)
    if (carril === -1) {
      carril = finDeCarril.length
      finDeCarril.push(finMs)
    } else {
      finDeCarril[carril] = finMs
    }
    barras.push({ item, carril, left: p.left, width: p.width })
  }

  return { barras, carriles: finDeCarril.length, fuera }
}
